/**
 * ScoreHeatmap Component
 *
 * Displays a grid showing the probability of each score type
 * for each hole on the course.
 */

import { useMemo } from 'react';
import {
    SCORE_TYPES,
    SCORE_LABELS_SHORT,
    SCORE_COLOURS
} from '../utility/golfConfig';
import { getHoleProbabilityMatrix } from '../utility/golfSimulation';

/**
 * Get alpha for probability value (higher probability = more opaque background)
 * Floor of 0.15 ensures even rare outcomes have visible cells
 */
const getProbabilityAlpha = (probability) => {
    return Math.max(0.15, Math.min(1.0, probability * 2));
};

/**
 * Convert hex colour + alpha to rgba string
 */
const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Format probability as percentage
 */
const formatProbability = (probability) => {
    const pct = probability * 100;
    if (pct < 1) return pct.toFixed(1);
    return Math.round(pct).toString();
};

/**
 * Individual cell in the heatmap
 */
const HeatmapCell = ({ probability, scoreType, holeNumber, par }) => {
    const baseColour = SCORE_COLOURS[scoreType];
    const alpha = getProbabilityAlpha(probability);
    const pctDisplay = formatProbability(probability);

    // Determine text colour based on background intensity
    const textColour = alpha > 0.5 ? '#ffffff' : '#374151';

    return (
        <td
            className="text-center text-xs border border-gray-200 relative"
            style={{
                backgroundColor: hexToRgba(baseColour, alpha),
                width: '40px',
                height: '28px'
            }}
            title={`Hole ${holeNumber} (Par ${par}): ${SCORE_LABELS_SHORT[scoreType]} - ${pctDisplay}%`}
        >
            <span className="relative z-10 font-medium" style={{ color: textColour }}>
                {probability >= 0.005 ? pctDisplay : ''}
            </span>
        </td>
    );
};

// Order score types from best to worst (constant, defined outside component)
const SCORE_TYPE_ORDER = [
    SCORE_TYPES.EAGLE_OR_BETTER,
    SCORE_TYPES.BIRDIE,
    SCORE_TYPES.PAR,
    SCORE_TYPES.BOGEY,
    SCORE_TYPES.DOUBLE_BOGEY,
    SCORE_TYPES.TRIPLE_OR_WORSE
];

/**
 * Main heatmap component
 */
export const ScoreHeatmap = ({ handicap, course, consistency }) => {
    const probabilityMatrix = useMemo(() => {
        return getHoleProbabilityMatrix(handicap, course, consistency);
    }, [handicap, course, consistency]);

    // Calculate average probabilities per score type
    const avgProbabilities = useMemo(() => {
        const avgs = {};
        for (const scoreType of SCORE_TYPE_ORDER) {
            const sum = probabilityMatrix.reduce(
                (acc, hole) => acc + hole.probabilities[scoreType],
                0
            );
            avgs[scoreType] = sum / probabilityMatrix.length;
        }
        return avgs;
    }, [probabilityMatrix]);

    return (
        <div>
            {/* Desktop layout: score types as rows, holes as columns */}
            <div className="hidden md:block overflow-x-auto">
                <table className="border-collapse text-sm mx-auto">
                    <thead>
                        {/* Hole numbers header */}
                        <tr>
                            <th className="px-2 py-1 text-left text-xs font-semibold bg-gray-100 border border-gray-200 sticky left-0 z-10">
                                Score
                            </th>
                            {probabilityMatrix.map((hole) => (
                                <th
                                    key={hole.hole}
                                    className={`px-1 py-1 text-center text-xs font-semibold border border-gray-200 ${
                                        hole.hole === 10 ? 'border-l-2 border-l-gray-400' : ''
                                    }`}
                                    style={{ backgroundColor: hole.hole <= 9 ? '#f3f4f6' : '#e5e7eb' }}
                                >
                                    {hole.hole}
                                </th>
                            ))}
                            <th className="px-2 py-1 text-center text-xs font-semibold bg-gray-200 border border-gray-200">
                                Avg
                            </th>
                        </tr>
                        {/* Par row */}
                        <tr>
                            <td className="px-2 py-1 text-left text-xs text-gray-500 bg-gray-50 border border-gray-200 sticky left-0 z-10">
                                Par
                            </td>
                            {probabilityMatrix.map((hole) => (
                                <td
                                    key={hole.hole}
                                    className={`px-1 py-1 text-center text-xs text-gray-500 bg-gray-50 border border-gray-200 ${
                                        hole.hole === 10 ? 'border-l-2 border-l-gray-400' : ''
                                    }`}
                                >
                                    {hole.par}
                                </td>
                            ))}
                            <td className="px-2 py-1 text-center text-xs text-gray-500 bg-gray-100 border border-gray-200">
                                -
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {SCORE_TYPE_ORDER.map((scoreType) => (
                            <tr key={scoreType}>
                                <td
                                    className="px-2 py-1 text-left text-xs font-medium border border-gray-200 sticky left-0 z-10"
                                    style={{
                                        backgroundColor: SCORE_COLOURS[scoreType],
                                        color: scoreType === SCORE_TYPES.DOUBLE_BOGEY ||
                                               scoreType === SCORE_TYPES.TRIPLE_OR_WORSE
                                            ? '#ffffff'
                                            : '#000000'
                                    }}
                                >
                                    {SCORE_LABELS_SHORT[scoreType]}
                                </td>
                                {probabilityMatrix.map((hole) => (
                                    <HeatmapCell
                                        key={hole.hole}
                                        probability={hole.probabilities[scoreType]}
                                        scoreType={scoreType}
                                        holeNumber={hole.hole}
                                        par={hole.par}
                                    />
                                ))}
                                {/* Average column */}
                                <td
                                    className="px-2 py-1 text-center text-xs font-semibold border border-gray-200 bg-gray-100"
                                >
                                    {formatProbability(avgProbabilities[scoreType])}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile layout: transposed — holes as rows, score types as columns */}
            <div className="md:hidden overflow-x-auto">
                <table className="border-collapse text-sm w-full">
                    <thead>
                        <tr>
                            <th className="px-2 py-1 text-center text-xs font-semibold bg-gray-100 border border-gray-200 w-12">
                                Hole
                            </th>
                            <th className="px-1 py-1 text-center text-xs text-gray-500 bg-gray-50 border border-gray-200 w-10">
                                Par
                            </th>
                            {SCORE_TYPE_ORDER.map((scoreType) => (
                                <th
                                    key={scoreType}
                                    className="px-1 py-1 text-center text-xs font-medium border border-gray-200"
                                    style={{
                                        backgroundColor: SCORE_COLOURS[scoreType],
                                        color: scoreType === SCORE_TYPES.DOUBLE_BOGEY ||
                                               scoreType === SCORE_TYPES.TRIPLE_OR_WORSE
                                            ? '#ffffff'
                                            : '#000000'
                                    }}
                                >
                                    {SCORE_LABELS_SHORT[scoreType]}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {probabilityMatrix.map((hole) => (
                            <tr key={hole.hole} className={hole.hole === 10 ? 'border-t-2 border-t-gray-400' : ''}>
                                <td className="px-2 py-1 text-center text-xs font-semibold border border-gray-200"
                                    style={{ backgroundColor: hole.hole <= 9 ? '#f3f4f6' : '#e5e7eb' }}
                                >
                                    {hole.hole}
                                </td>
                                <td className="px-1 py-1 text-center text-xs text-gray-500 bg-gray-50 border border-gray-200">
                                    {hole.par}
                                </td>
                                {SCORE_TYPE_ORDER.map((scoreType) => (
                                    <HeatmapCell
                                        key={scoreType}
                                        probability={hole.probabilities[scoreType]}
                                        scoreType={scoreType}
                                        holeNumber={hole.hole}
                                        par={hole.par}
                                    />
                                ))}
                            </tr>
                        ))}
                        {/* Averages row */}
                        <tr className="border-t-2 border-t-gray-300">
                            <td className="px-2 py-1 text-center text-xs font-semibold bg-gray-200 border border-gray-200" colSpan={2}>
                                Avg
                            </td>
                            {SCORE_TYPE_ORDER.map((scoreType) => (
                                <td
                                    key={scoreType}
                                    className="px-1 py-1 text-center text-xs font-semibold border border-gray-200 bg-gray-100"
                                >
                                    {formatProbability(avgProbabilities[scoreType])}%
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Hole type summary */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
                <HoleTypeSummary
                    title="Par 3s"
                    holes={probabilityMatrix.filter(h => h.par === 3)}
                />
                <HoleTypeSummary
                    title="Par 4s"
                    holes={probabilityMatrix.filter(h => h.par === 4)}
                />
                <HoleTypeSummary
                    title="Par 5s"
                    holes={probabilityMatrix.filter(h => h.par === 5)}
                />
            </div>
        </div>
    );
};

/**
 * Summary for a hole type (par 3/4/5)
 */
const HoleTypeSummary = ({ title, holes }) => {
    if (holes.length === 0) return null;

    // Calculate average par probability for this hole type
    const avgParProb = holes.reduce(
        (sum, h) => sum + h.probabilities[SCORE_TYPES.PAR],
        0
    ) / holes.length;

    const avgBirdieProb = holes.reduce(
        (sum, h) => sum + h.probabilities[SCORE_TYPES.BIRDIE] + h.probabilities[SCORE_TYPES.EAGLE_OR_BETTER],
        0
    ) / holes.length;

    const avgBogeyPlusProb = holes.reduce(
        (sum, h) => sum + h.probabilities[SCORE_TYPES.BOGEY] +
                    h.probabilities[SCORE_TYPES.DOUBLE_BOGEY] +
                    h.probabilities[SCORE_TYPES.TRIPLE_OR_WORSE],
        0
    ) / holes.length;

    return (
        <div className="bg-gray-50 rounded-lg p-3">
            <div className="font-semibold text-gray-700 mb-2">{title}</div>
            <div className="text-xs space-y-1">
                <div className="flex justify-between">
                    <span className="text-gray-500">Birdie+:</span>
                    <span className="font-medium" style={{ color: SCORE_COLOURS[SCORE_TYPES.BIRDIE] }}>
                        {formatProbability(avgBirdieProb)}%
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">Par:</span>
                    <span className="font-medium" style={{ color: SCORE_COLOURS[SCORE_TYPES.PAR] }}>
                        {formatProbability(avgParProb)}%
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">Bogey+:</span>
                    <span className="font-medium text-gray-600">
                        {formatProbability(avgBogeyPlusProb)}%
                    </span>
                </div>
            </div>
        </div>
    );
};

/**
 * Compact probability summary for quick view
 */
export const ProbabilitySummary = ({ handicap, course, consistency }) => {
    const probabilityMatrix = useMemo(() => {
        return getHoleProbabilityMatrix(handicap, course, consistency);
    }, [handicap, course, consistency]);

    // Calculate overall averages
    const avgProbabilities = useMemo(() => {
        const avgs = {};
        for (const scoreType of Object.values(SCORE_TYPES)) {
            const sum = probabilityMatrix.reduce(
                (acc, hole) => acc + hole.probabilities[scoreType],
                0
            );
            avgs[scoreType] = sum / probabilityMatrix.length;
        }
        return avgs;
    }, [probabilityMatrix]);

    // Calculate expected scores per hole type
    const expectedPerHole = probabilityMatrix.reduce((sum, hole) => {
        let expected = 0;
        expected += hole.probabilities[SCORE_TYPES.EAGLE_OR_BETTER] * (hole.par - 2);
        expected += hole.probabilities[SCORE_TYPES.BIRDIE] * (hole.par - 1);
        expected += hole.probabilities[SCORE_TYPES.PAR] * hole.par;
        expected += hole.probabilities[SCORE_TYPES.BOGEY] * (hole.par + 1);
        expected += hole.probabilities[SCORE_TYPES.DOUBLE_BOGEY] * (hole.par + 2);
        expected += hole.probabilities[SCORE_TYPES.TRIPLE_OR_WORSE] * (hole.par + 3);
        return sum + expected;
    }, 0);

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-700 mb-3">
                Expected Scoring Breakdown
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                {Object.values(SCORE_TYPES).map((scoreType) => (
                    <div key={scoreType} className="text-xs">
                        <div
                            className="w-full py-1 rounded font-medium"
                            style={{
                                backgroundColor: SCORE_COLOURS[scoreType],
                                color: scoreType === SCORE_TYPES.DOUBLE_BOGEY ||
                                       scoreType === SCORE_TYPES.TRIPLE_OR_WORSE
                                    ? '#ffffff'
                                    : '#000000'
                            }}
                        >
                            {SCORE_LABELS_SHORT[scoreType]}
                        </div>
                        <div className="mt-1 font-semibold">
                            {formatProbability(avgProbabilities[scoreType])}%
                        </div>
                        <div className="text-gray-500">
                            ~{Math.round(avgProbabilities[scoreType] * 18 * 10) / 10}/round
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 text-center text-sm">
                <span className="text-gray-500">Expected round score: </span>
                <span className="font-bold text-green-700">
                    {Math.round(expectedPerHole)}
                </span>
            </div>
        </div>
    );
};
